import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { MessageSquarePlus, ShoppingBasket, Star } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type PackageKey = "pin" | "isim" | "normal" | "premium" | "iliski" | "test" | string;

type Package = {
  id: string;
  key: PackageKey;
  name: string;
  description: string;
  basePriceTry: number;
  discountedPriceTry?: number;
  active: boolean;
};

type CatalogResponse = {
  ok: true;
  packages: Package[];
  products: unknown[];
  settings: unknown;
};

type CartItem = {
  packageKey: PackageKey;
  qty: number;
};

type Review = {
  id: string;
  packageKey: string;
  name: string;
  rating: number;
  message: string;
  createdAt: number;
};

type ReviewsResponse = { ok: true; reviews: Review[] };

type CreateReviewResponse = { ok: true } | { ok: false; error: string };

const GOLD = "#d4af37";
const CART_STORAGE_KEY = "numeroloji_cart_v1";

function safeParseCart(raw: string | null): CartItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((it) => {
        const obj = it as any;
        const packageKey = typeof obj?.packageKey === "string" ? (obj.packageKey as PackageKey) : null;
        const qty = typeof obj?.qty === "number" ? obj.qty : Number(obj?.qty);
        if (!packageKey) return null;
        if (!Number.isFinite(qty) || qty <= 0) return null;
        return { packageKey, qty: Math.min(99, Math.trunc(qty)) } satisfies CartItem;
      })
      .filter(Boolean) as CartItem[];
  } catch {
    return [];
  }
}

function loadCart(): CartItem[] {
  return safeParseCart(globalThis?.localStorage?.getItem(CART_STORAGE_KEY) ?? null);
}

function saveCart(items: CartItem[]) {
  globalThis?.localStorage?.setItem(CART_STORAGE_KEY, JSON.stringify(items));
}

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await fetch("/api/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function fetchReviews(packageKey: string): Promise<ReviewsResponse> {
  const res = await fetch(`/api/numeroloji/reviews?packageKey=${encodeURIComponent(packageKey)}`);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Yorumlar alınamadı.");
  }
  return json as ReviewsResponse;
}

async function createReview(payload: {
  packageKey: string;
  name: string;
  rating: number;
  message: string;
}): Promise<CreateReviewResponse> {
  const res = await fetch("/api/numeroloji/reviews", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    return {
      ok: false as const,
      error: typeof json?.error === "string" ? json.error : "Yorum gönderilemedi.",
    };
  }
  return json as CreateReviewResponse;
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ₺`;
  }
}

function heroArt(key: PackageKey) {
  switch (key) {
    case "pin":
      return "[background:radial-gradient(circle_at_25%_25%,rgba(212,175,55,0.30)_0%,transparent_55%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "isim":
      return "[background:radial-gradient(circle_at_65%_30%,rgba(127,13,242,0.32)_0%,transparent_55%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "iliski":
      return "[background:radial-gradient(circle_at_50%_20%,rgba(212,175,55,0.22)_0%,transparent_60%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "premium":
      return "[background:radial-gradient(circle_at_40%_25%,rgba(212,175,55,0.25)_0%,transparent_60%),radial-gradient(circle_at_70%_65%,rgba(127,13,242,0.22)_0%,transparent_65%),linear-gradient(145deg,#151518,#0a0a0c)]";
    default:
      return "[background:radial-gradient(circle_at_40%_25%,rgba(127,13,242,0.22)_0%,transparent_60%),linear-gradient(145deg,#151518,#0a0a0c)]";
  }
}

function keyFeatures(key: PackageKey): string[] {
  switch (key) {
    case "iliski":
      return [
        "Uyum analizi (2 kişi)",
        "Pin kodları (iki kişi)",
        "Uyum alanları ve dikkat noktaları",
      ];
    case "isim":
      return ["İsim titreşimi analizi", "Karmik borç göstergeleri", "Özet tablo"];
    case "pin":
      return ["Pin kodları", "Element dengesi", "Özet tablo"];
    case "premium":
      return ["Genişletilmiş analiz", "Pin kodları + element dengesi", "Özet tablo ve ek yorumlar"];
    default:
      return ["Kader sayısı analizi", "Pin kodları", "Özet tablo"];
  }
}

function learnList(key: PackageKey) {
  if (key === "iliski") {
    return [
      "İlişkide uyumlu ve zorlayıcı alanlar",
      "Birlikte güçlü olduğunuz temalar",
      "Dönüştürmeniz gereken tekrar eden dersler",
      "İki kişi pin kodlarından kısa özet",
    ];
  }

  return [
    "Karakterinizin temel titreşimi",
    "Karmik borç göstergeleri (varsa)",
    "Pin kodları ve yaşamın ana temaları",
    "Element dengesi (kısa)",
  ];
}

function Stars({ rating, className }: { rating: number; className?: string }) {
  const clamped = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <div className={cn("flex items-center gap-1", className)} aria-label={`Puan: ${clamped}/5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn("h-4 w-4", i < clamped ? "fill-current" : "fill-transparent")}
          style={{ color: i < clamped ? GOLD : "rgba(212,175,55,0.25)" }}
        />
      ))}
    </div>
  );
}

export default function NumerolojiPaketDetay() {
  const navigate = useNavigate();
  const params = useParams();
  const packageKeyParam = (params.key ?? "").trim();

  const [cart, setCart] = useState<CartItem[]>(() => loadCart());

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const cartCount = cart.reduce((acc, it) => acc + (Number.isFinite(it.qty) ? it.qty : 0), 0);

  const catalogQuery = useQuery({
    queryKey: ["numeroloji-catalog"],
    queryFn: fetchCatalog,
    staleTime: 60_000,
  });

  const packages = (catalogQuery.data?.packages ?? []).filter((p) => p.active && p.key !== "test");

  const pkg = useMemo(() => {
    return packages.find((p) => String(p.key) === packageKeyParam) ?? null;
  }, [packageKeyParam, packages]);

  const reviewsQuery = useQuery({
    queryKey: ["numeroloji-reviews", packageKeyParam],
    queryFn: () => fetchReviews(packageKeyParam),
    enabled: Boolean(packageKeyParam),
    staleTime: 30_000,
  });

  const approvedReviews = reviewsQuery.data?.reviews ?? [];

  const ratingAvg = useMemo(() => {
    if (!approvedReviews.length) return 0;
    const total = approvedReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0);
    return total / approvedReviews.length;
  }, [approvedReviews]);

  const [reviewName, setReviewName] = useState("");
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewSent, setReviewSent] = useState<null | "ok" | string>(null);

  const reviewMutation = useMutation({
    mutationFn: createReview,
    onSuccess: (res) => {
      if (res.ok === false) {
        setReviewSent(res.error);
        return;
      }
      setReviewSent("ok");
      setReviewName("");
      setReviewMessage("");
      setReviewRating(5);
    },
  });

  const key = (pkg?.key ?? packageKeyParam) as PackageKey;
  const features = useMemo(() => keyFeatures(key), [key]);
  const learn = useMemo(() => learnList(key), [key]);

  const inCart = pkg ? cart.some((it) => it.packageKey === pkg.key) : false;

  const price =
    pkg && typeof pkg.discountedPriceTry === "number" && pkg.discountedPriceTry > 0
      ? pkg.discountedPriceTry
      : (pkg?.basePriceTry ?? 0);
  const priceOld =
    pkg && typeof pkg.discountedPriceTry === "number" && pkg.discountedPriceTry > 0 && pkg.discountedPriceTry < pkg.basePriceTry
      ? pkg.basePriceTry
      : 0;

  function addToCartAndGoCart() {
    if (!pkg) return;
    setCart((prev) => {
      const existing = prev.find((it) => it.packageKey === pkg.key);
      if (!existing) return [...prev, { packageKey: pkg.key, qty: 1 }];
      return prev.map((it) => (it.packageKey === pkg.key ? { ...it, qty: Math.min(99, it.qty + 1) } : it));
    });
    navigate("/numeroloji/sepet");
  }

  return (
    <div className="min-h-dvh bg-[#050508] text-slate-200">
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#050508]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
          <Link to="/numeroloji" className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d4af37] rotate-45">
              <span className="h-3 w-3 bg-[#d4af37]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[0.16em] text-white">Numeroloji.app</span>
          </Link>

          <div className="flex items-center gap-6 sm:gap-8">
            <Link
              to="/numeroloji/magaza"
              className="font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:text-sm"
            >
              MAĞAZA
            </Link>
            <Link
              to="/numeroloji#kasalar"
              className="hidden font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:block sm:text-sm"
            >
              ANALİZLER
            </Link>

            <Link to="/numeroloji/sepet" className="relative" aria-label="Sepet">
              <ShoppingBasket className="h-5 w-5 text-slate-200" />
              {cartCount ? (
                <span
                  className="absolute -right-2 -top-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-black"
                  style={{ backgroundColor: GOLD }}
                  aria-label={`Sepette ${cartCount} ürün`}
                >
                  {cartCount}
                </span>
              ) : null}
            </Link>
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:py-12">
        <div className="mb-6 text-xs opacity-60">
          <Link to="/numeroloji" className="hover:opacity-100">
            Ana sayfa
          </Link>
          <span className="px-2">/</span>
          <Link to="/numeroloji/magaza" className="hover:opacity-100">
            Numeroloji Analizleri
          </Link>
          <span className="px-2">/</span>
          <span className="opacity-90">{pkg?.name ?? "Ürün"}</span>
        </div>

        {catalogQuery.isLoading ? (
          <div className="rounded-xl border p-6 text-sm text-slate-300" style={{ borderColor: "rgba(212,175,55,0.12)" }}>
            Yükleniyor...
          </div>
        ) : null}

        {catalogQuery.isError ? (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
            {(catalogQuery.error as Error).message}
          </div>
        ) : null}

        {!catalogQuery.isLoading && !catalogQuery.isError && !pkg ? (
          <div className="rounded-xl border p-6 text-sm text-slate-300" style={{ borderColor: "rgba(212,175,55,0.12)" }}>
            Paket bulunamadı.
          </div>
        ) : null}

        {pkg ? (
          <>
            <section className="grid gap-8 lg:grid-cols-[420px_1fr] lg:items-start">
              <div>
                <div
                  className={cn(
                    "relative overflow-hidden rounded-xl border",
                    "shadow-[0_10px_30px_-10px_rgba(212,175,55,0.18)]",
                    heroArt(pkg.key),
                  )}
                  style={{ borderColor: "rgba(212,175,55,0.20)" }}
                >
                  <div className="absolute inset-0 opacity-70 [background:radial-gradient(circle_at_top,rgba(127,13,242,0.20)_0%,transparent_60%)]" />
                  <div className="relative aspect-[4/5] p-6">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="font-display text-xs uppercase tracking-[0.25em]" style={{ color: GOLD }}>
                        {String(pkg.key).toUpperCase()}
                      </div>
                      <div className="mt-2 font-display text-xl tracking-wide text-white">{pkg.name}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex gap-3">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <div
                      // decorative thumbs
                      key={idx}
                      className={cn("h-16 w-16 overflow-hidden rounded border", heroArt(pkg.key))}
                      style={{ borderColor: "rgba(212,175,55,0.12)" }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className="inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em]"
                    style={{
                      borderColor: "rgba(212,175,55,0.35)",
                      backgroundColor: "rgba(212,175,55,0.10)",
                      color: GOLD,
                    }}
                  >
                    {pkg.key === "premium" || pkg.key === "pin" ? "EN ÇOK TERCİH EDİLEN" : "ÖNERİLEN"}
                  </span>

                  <div className="flex items-center gap-2">
                    <Stars rating={ratingAvg || 5} />
                    <span className="text-xs opacity-60">({approvedReviews.length} Değerlendirme)</span>
                  </div>
                </div>

                <h1 className="mt-4 font-display text-4xl tracking-tight text-white sm:text-5xl">{pkg.name}</h1>

                <div className="mt-5 flex flex-wrap items-end gap-3">
                  <div className="font-display text-2xl" style={{ color: GOLD }}>
                    {formatTry(price)}
                  </div>
                  {priceOld ? (
                    <div className="text-sm opacity-50 line-through">{formatTry(priceOld)}</div>
                  ) : null}
                </div>

                <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-200/90 sm:text-base">
                  {pkg.description}
                </p>

                <div className="mt-6 grid gap-3">
                  {features.map((f) => (
                    <div key={f} className="flex items-start gap-3 text-sm">
                      <span
                        className="mt-1.5 h-2 w-2 rounded-full"
                        style={{ backgroundColor: "rgba(212,175,55,0.90)" }}
                      />
                      <span className="text-slate-200">{f}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    onClick={addToCartAndGoCart}
                    className="h-12 rounded-xl px-8 font-display text-xs font-bold uppercase tracking-[0.22em] text-black"
                    style={{ backgroundColor: GOLD }}
                  >
                    Hemen Analizini Başlat
                  </Button>

                  <Button
                    asChild
                    variant="outline"
                    className={cn(
                      "h-12 rounded-xl border-[rgba(212,175,55,0.35)] bg-transparent",
                      "font-display text-xs uppercase tracking-[0.22em] text-slate-200",
                      "hover:bg-[rgba(212,175,55,0.10)]",
                    )}
                  >
                    <Link to="/numeroloji/magaza">Mağazaya dön</Link>
                  </Button>

                  <div className="text-xs opacity-60">
                    {inCart ? "Bu paket sepette." : "Sepete ekleyip ödemeye geçebilirsin."}
                  </div>
                </div>

                <div className="mt-3 text-[11px] opacity-60">Bu rapor son 24 saatte birkaç kişi tarafından incelendi.</div>
              </div>
            </section>

            <section className="mt-14">
              <h2 className="text-center font-display text-2xl tracking-tight" style={{ color: GOLD }}>
                Analiz Detayları
              </h2>

              <div
                className="mx-auto mt-6 max-w-4xl overflow-hidden rounded-xl border"
                style={{
                  borderColor: "rgba(212,175,55,0.12)",
                  background: "linear-gradient(145deg, #151518, #0a0a0c)",
                }}
              >
                <Accordion type="single" collapsible className="px-5">
                  <AccordionItem value="learn" className="border-[rgba(212,175,55,0.12)]">
                    <AccordionTrigger className="font-display text-sm tracking-wide text-slate-100 hover:no-underline">
                      Neler öğreneceksiniz?
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-200">
                      <ul className="grid gap-2 pl-5 text-sm">
                        {learn.map((l) => (
                          <li key={l} className="list-disc opacity-90">
                            {l}
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="delivery" className="border-[rgba(212,175,55,0.12)]">
                    <AccordionTrigger className="font-display text-sm tracking-wide text-slate-100 hover:no-underline">
                      Teslimat Süreci
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-200">
                      <div className="grid gap-2 text-sm opacity-90">
                        <div>1) Ödemeyi tamamla.</div>
                        <div>2) Formu doldur (gerekli alanlara göre).</div>
                        <div>3) Raporun PDF olarak e-posta ile gönderilsin.</div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="method" className="border-[rgba(212,175,55,0.12)]">
                    <AccordionTrigger className="font-display text-sm tracking-wide text-slate-100 hover:no-underline">
                      Metodoloji
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-200">
                      <div className="text-sm opacity-90">
                        Modern numeroloji hesaplamaları (kader yolu, pin kodları, element dengesi ve isim titreşimi)
                        birleştirilir; sonuçlar düzenli bir özet tablo ile sunulur.
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </section>

            <section id="yorumlar" className="mt-14">
              <div className="mx-auto flex max-w-6xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-display text-2xl tracking-tight" style={{ color: GOLD }}>
                    Danışan Yorumları
                  </h2>
                  <div className="mt-2 text-sm opacity-60">Bu analizi alan kullanıcılarımızın deneyimleri</div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="h-11 rounded-xl border-[rgba(212,175,55,0.35)] bg-transparent font-display text-xs uppercase tracking-[0.22em] text-slate-200 hover:bg-[rgba(212,175,55,0.10)]"
                  onClick={() => {
                    document.getElementById("yorum-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  <MessageSquarePlus className="mr-2 h-4 w-4" style={{ color: GOLD }} />
                  Yorum Yap
                </Button>
              </div>

              {reviewsQuery.isError ? (
                <div className="mx-auto mt-6 max-w-6xl rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
                  {(reviewsQuery.error as Error).message}
                </div>
              ) : null}

              <div className="mx-auto mt-8 grid max-w-6xl gap-6 md:grid-cols-3">
                {(approvedReviews.length ? approvedReviews : []).map((r) => (
                  <div
                    key={r.id}
                    className="rounded-xl border p-5"
                    style={{
                      background: "linear-gradient(145deg, #151518, #0a0a0c)",
                      borderColor: "rgba(212,175,55,0.12)",
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="grid h-10 w-10 place-items-center rounded-full border"
                        style={{ borderColor: "rgba(212,175,55,0.18)", backgroundColor: "rgba(212,175,55,0.08)" }}
                      >
                        <span className="text-sm font-semibold" style={{ color: GOLD }}>
                          {(r.name || "?").slice(0, 1).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white line-clamp-1">{r.name || "Anonim"}</div>
                        <Stars rating={r.rating} className="mt-1" />
                      </div>
                    </div>

                    <div className="mt-4 text-sm leading-relaxed text-slate-200/90">“{r.message}”</div>
                  </div>
                ))}

                {!approvedReviews.length && !reviewsQuery.isLoading ? (
                  <div
                    className="rounded-xl border p-8 text-center text-sm text-slate-300 md:col-span-3"
                    style={{ borderColor: "rgba(212,175,55,0.12)" }}
                  >
                    Henüz onaylı yorum yok. İlk yorumu sen bırak.
                  </div>
                ) : null}
              </div>

              <div id="yorum-form" className="mx-auto mt-10 max-w-3xl">
                <div
                  className="rounded-xl border p-6"
                  style={{
                    borderColor: "rgba(212,175,55,0.12)",
                    background: "linear-gradient(145deg, #151518, #0a0a0c)",
                  }}
                >
                  <div className="font-display text-base tracking-wide text-white">Yorumunu bırak</div>
                  <div className="mt-1 text-xs opacity-60">Yorumun yönetici onayından sonra yayına alınır.</div>

                  <div className="mt-5 grid gap-4">
                    <div className="grid gap-2">
                      <Label className="text-slate-200">Adın</Label>
                      <Input
                        value={reviewName}
                        onChange={(e) => setReviewName(e.target.value)}
                        placeholder="Örn: Elif Y."
                        className="h-11 border-0 bg-black/40 text-slate-100 placeholder:text-slate-500"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label className="text-slate-200">Puan</Label>
                      <div className="flex items-center gap-2">
                        {Array.from({ length: 5 }).map((_, i) => {
                          const value = i + 1;
                          const active = value <= reviewRating;
                          return (
                            <button
                              type="button"
                              key={value}
                              onClick={() => setReviewRating(value)}
                              className="transition-transform hover:scale-110"
                              aria-label={`${value} yıldız`}
                            >
                              <Star
                                className={cn("h-5 w-5", active ? "fill-current" : "fill-transparent")}
                                style={{ color: active ? GOLD : "rgba(212,175,55,0.25)" }}
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label className="text-slate-200">Yorum</Label>
                      <textarea
                        value={reviewMessage}
                        onChange={(e) => setReviewMessage(e.target.value)}
                        placeholder="Deneyimini kısaca yaz..."
                        rows={4}
                        className="w-full rounded-md bg-black/40 p-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none ring-1 ring-transparent focus:ring-[rgba(212,175,55,0.35)]"
                      />
                    </div>

                    {reviewSent && reviewSent !== "ok" ? (
                      <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                        {reviewSent}
                      </div>
                    ) : null}

                    {reviewSent === "ok" ? (
                      <div
                        className="rounded-lg border p-3 text-sm"
                        style={{ borderColor: "rgba(212,175,55,0.20)", backgroundColor: "rgba(212,175,55,0.10)" }}
                      >
                        Teşekkürler! Yorumun onaydan sonra yayına alınacak.
                      </div>
                    ) : null}

                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button
                        onClick={() => {
                          if (reviewMutation.isPending) return;
                          setReviewSent(null);
                          reviewMutation.mutate({
                            packageKey: String(pkg.key),
                            name: reviewName.trim(),
                            rating: reviewRating,
                            message: reviewMessage.trim(),
                          });
                        }}
                        className="h-11 rounded-xl px-8 font-display text-xs font-bold uppercase tracking-[0.22em] text-black"
                        style={{ backgroundColor: GOLD }}
                        disabled={!reviewMessage.trim() || !reviewName.trim()}
                      >
                        {reviewMutation.isPending ? "Gönderiliyor..." : "Gönder"}
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 rounded-xl border-[rgba(212,175,55,0.35)] bg-transparent font-display text-xs uppercase tracking-[0.22em] text-slate-200 hover:bg-[rgba(212,175,55,0.10)]"
                        onClick={() => {
                          setReviewName("");
                          setReviewMessage("");
                          setReviewRating(5);
                          setReviewSent(null);
                        }}
                      >
                        Temizle
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        ) : null}
      </main>

      <footer className="mt-20 border-t py-12" style={{ borderColor: "rgba(212,175,55,0.10)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4">
          <div className="h-24 w-px bg-gradient-to-b from-[rgba(212,175,55,0.95)] to-transparent" />
          <div className="text-center">
            <div className="font-display text-3xl tracking-widest" style={{ color: GOLD }}>
              Numeroloji.app
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-40">
              Evrensel Sayılar Rehberi © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
